import { EventsDateFilter, type EventUIModel } from "~/types/events.types";
import { EventStatus } from "~/types/enums";
import type { Database, Tables } from "~/types/types.gen";
import type { UploadedFile } from "~/components/common/FileDropper.vue";

export default function useEventsDatabase() {
  const client = useSupabaseClient<Database>();
  const posthog = usePosthog();
  const selectStatement =
    "*, author:profiles(id, full_name, avatar_url), author_org:organisations(id, name, avatar_url), author_gym:gyms(id, name, avatar_url)";

  async function fetchEvents(
    dateFilter: EventsDateFilter,
    searchQuery: string | null,
  ): Promise<EventUIModel[] | null> {
    const currentDate = new Date();

    // Canceled events are kept for their registrations, but not listed.
    const query = client
      .from("events")
      .select(selectStatement)
      .eq("event_status", EventStatus.ACTIVE);

    if (dateFilter == EventsDateFilter.UPCOMING) {
      // Include events that haven't ended yet (upcoming or in progress)
      query.gte("datetime_end", currentDate.toJSON());
      query.order("datetime_start", { ascending: true });
    } else {
      query.lt("datetime_end", currentDate.toJSON());
      query.order("datetime_end", { ascending: false });
    }

    if (searchQuery) {
      query.ilike("name", `%${searchQuery}%`);
    }

    const { data, error } = await query;

    posthog.logError(error, "useEventsDatabase:fetchEvents");

    if (error) throw error;

    return data as EventUIModel[] | null;
  }

  async function fetchEvent(eventId: string): Promise<EventUIModel | null> {
    return await $fetch<EventUIModel>(`/api/events/${eventId}`);
  }

  async function fetchManageEvent(
    eventId: string,
  ): Promise<EventUIModel | null> {
    return await usePrivateApiFetch()<EventUIModel>(
      `/api/events/${eventId}/manage`,
    );
  }

  async function insertEvent(
    newEvent: Tables<"events">,
  ): Promise<EventUIModel> {
    const { data, error } = await client
      .from("events")
      .insert(newEvent as never)
      .select(selectStatement)
      .single();

    posthog.logError(error, "useEventsDatabase:insertEvent");
    if (error) throw error;

    return data as EventUIModel;
  }

  async function updateEvent(
    newData: Partial<Tables<"events">>,
  ): Promise<EventUIModel> {
    const { data, error } = await client
      .from("events")
      .update(newData as never)
      .eq("id", newData.id)
      .select(selectStatement)
      .single();

    posthog.logError(error, "useEventsDatabase:updateEvent");
    if (error) throw error;

    return data as EventUIModel;
  }

  // Events are never deleted, only canceled.
  async function cancelEvent(eventId: string) {
    return await usePrivateApiFetch()<{
      id: string;
      event_status: EventStatus;
      rejected_registrations: number;
    }>(`/api/events/${eventId}/cancel`, { method: HttpMethod.POST });
  }

  async function uploadAttachments(
    eventId: string,
    attachments: UploadedFile[],
  ): Promise<UploadedFile[]> {
    var updatedAttachments: UploadedFile[] = attachments;

    const attachmentsForUpload = attachments.filter(
      (attachment) => attachment.file != null,
    );

    const promises = attachmentsForUpload.map(async (f, i) => {
      if (!f.file || f.url) {
        return;
      }

      try {
        const response =
          await useBackblazeService().uploadFile(f.file, `events/${eventId}`);

        if (response.fileId != null && response.fileUrl != null) {
          updatedAttachments.find((att) => att.name === f.name)!.id =
            response.fileId;

          updatedAttachments.find((att) => att.name === f.name)!.url =
            response.fileUrl;
        }
      } catch (error) {
        f.error = true;

        posthog.logError(
          error,
          "useEventsDatabase:uploadAttachments",
          `Error uploading file: ${f.name} ${f.type}`,
        );
      }
    });

    await Promise.all(promises);

    return updatedAttachments;
  }

  async function fetchEventCategories(): Promise<Tables<"event_categories">[]> {
    const { data, error } = await client.from("event_categories").select();

    posthog.logError(error, "useEventsDatabase:fetchEventCategories");

    return data ?? [];
  }

  return {
    fetchEvents,
    fetchEvent,
    fetchManageEvent,
    insertEvent,
    updateEvent,
    cancelEvent,
    uploadAttachments,
    fetchEventCategories,
  };
}
